import type { TData, TDataCell } from "../types";
import { findLastFilledColumn } from "./find-last-column";
import { findLastFilledRow } from "./find-last-row";

export const normalizeData = ({ data: original }: { data: TData }) => {
  // A chart saved without data, or with a row that is not a list, draws
  // nothing rather than taking the editor down with it (QA, 2026-10-10: an
  // agent-made chart with no data crashed every load of the deck).
  if (!Array.isArray(original)) return [];
  const data = original.map((row) => (Array.isArray(row) ? [...row] : []));
  const lastRowIndex = findLastFilledRow(data);
  if (lastRowIndex === -1) return [];
  const validData = data.slice(0, lastRowIndex + 1);
  const lastColumnIndex = findLastFilledColumn(validData);

  return validData.map((row, rowIndex) =>
    normalizeRow(row, lastColumnIndex, rowIndex),
  );
};

const normalizeRow = (
  row: TDataCell[],
  lastColumnIndex: number,
  rowIndex: number,
): Record<string, string>[] => {
  // eslint-disable-next-line no-param-reassign
  row.length = lastColumnIndex + 1;
  const mapped = [] as Record<string, string>[];

  if (rowIndex === 0) {
    for (let i = 0; i < row.length; i++) {
      const value = getValue(row[i]);
      if (i === 0) mapped.push({ value: "" });
      else
        mapped.push({
          ...(row[i] || {}),
          value: value || "Series #" + i,
        });
    }
  } else {
    for (let j = 0; j < row.length; j++) {
      if (j === 0) {
        mapped.push({
          ...(row[j] || {}),
          value: getValue(row[j]) || "Series title #" + rowIndex,
        });
      } else
        mapped.push({
          ...(row[j] || {}),
          value: getNumberValue(row[j]) || "0",
        });
    }
  }

  return mapped;
};

const getValue = (cell: TDataCell) => {
  const variable = cell?.variable;
  const isParsed =
    variable && !variable.startsWith("{{") && !variable.endsWith("}}");
  return isParsed ? variable : cell?.value;
};

const isDynamicPlaceholder = (value: string) => /^\[.+\]$/.test(value);

const getNumberValue = (cell: TDataCell) => {
  const value = getValue(cell);
  if (value && isDynamicPlaceholder(value)) return value;
  const isNumber = !isNaN(Number(value));
  return isNumber ? value : "0";
};
